import { FONT_ASSET_VERSION } from '@tiny-fox/storybook-renderer';

export const STORY_TEXT_LINE_HEIGHT = 1.35;
export const STORY_TEXT_PADDING = 10;
export const STORY_BUBBLE_PADDING = 15;
export const PRODUCTION_FONT_ASSET_VERSION = FONT_ASSET_VERSION;

export type StoryFontStyle = 'normal' | 'italic';

export interface StoryTextTypography {
  fontFamily: string;
  fontWeight: string;
  fontStyle: StoryFontStyle;
  lineHeight: number;
  padding: number;
  fontAssetVersion: string;
}

export interface TextBlockMeasurementInput {
  content: string;
  width: number;
  fontSize: number;
  typography: StoryTextTypography;
}

export interface TextBlockMeasurement {
  height: number;
  lineCount: number;
  typography: StoryTextTypography;
}

export type TextBlockMeasurer = (
  input: TextBlockMeasurementInput
) => TextBlockMeasurement;

export interface ProductionFontSample {
  content: string;
  fontSize: number;
  typography: StoryTextTypography;
}

const SCRIPT_FALLBACK_FONT_FAMILIES = [
  'Noto Sans',
  'Noto Sans Arabic',
  'Noto Sans Devanagari',
  'Noto Sans Hebrew',
  'Noto Sans JP',
  'Noto Sans KR',
  'Noto Sans SC',
  'Noto Sans Thai',
  'Noto Color Emoji'
] as const;
type ScriptFallbackFontFamily = typeof SCRIPT_FALLBACK_FONT_FAMILIES[number];

const PRODUCTION_FONT_FAMILIES = new Set([
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Oswald',
  'Raleway',
  'Ubuntu',
  'Merriweather',
  'Playfair Display',
  'Pacifico',
  'Dancing Script',
  'Indie Flower',
  'Permanent Marker',
  'Caveat',
  'Fredoka One',
  'Bangers',
  'Righteous',
  'Audiowide',
  'Press Start 2P',
  'Bebas Neue',
  'Shadows Into Light',
  'Patrick Hand',
  'Architects Daughter',
  'Kalam'
]);

const FONT_SUBSTITUTIONS: Record<string, string> = {
  'Comic Sans MS': 'Patrick Hand',
  Arial: 'Roboto',
  Helvetica: 'Open Sans',
  Verdana: 'Lato',
  Tahoma: 'Ubuntu',
  'Trebuchet MS': 'Montserrat',
  'Arial Black': 'Oswald',
  Impact: 'Bebas Neue',
  Georgia: 'Merriweather',
  'Times New Roman': 'Playfair Display',
  Palatino: 'Merriweather',
  'Courier New': 'Roboto',
  Consolas: 'Ubuntu',
  Monaco: 'Ubuntu',
  Papyrus: 'Caveat',
  'Brush Script MT': 'Dancing Script'
};

const stripFontQuotes = (fontFamily: string) => fontFamily
  .trim()
  .replace(/^['"]|['"]$/g, '');

const primaryFontFamily = (fontFamily: string) => stripFontQuotes(
  String(fontFamily || 'Roboto').split(',')[0]
);

export const resolveProductionFontFamily = (fontFamily: string) => {
  const primaryFamily = primaryFontFamily(fontFamily);
  return FONT_SUBSTITUTIONS[primaryFamily] || primaryFamily || 'Roboto';
};

export const segmentGraphemes = (content: string): string[] => {
  const value = String(content || '');
  const Segmenter = (Intl as any).Segmenter;
  if (typeof Segmenter === 'function') {
    const segmenter = new Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(value), (entry: any) => entry.segment);
  }

  // Modern production browsers provide Intl.Segmenter. This fallback still
  // keeps combining marks and joiner sequences attached for older browsers.
  return Array.from(value).reduce<string[]>((segments, codePoint) => {
    if (segments.length === 0) return [codePoint];
    if (/^[\p{Mark}\uFE0E\uFE0F\u200D]$/u.test(codePoint)
      || segments[segments.length - 1].endsWith('\u200D')) {
      segments[segments.length - 1] += codePoint;
    } else {
      segments.push(codePoint);
    }
    return segments;
  }, []);
};

const fallbackFamilyForGrapheme = (
  grapheme: string,
  hanFallbackFamily: Extract<
    ScriptFallbackFontFamily,
    'Noto Sans JP' | 'Noto Sans KR' | 'Noto Sans SC'
  >
): ScriptFallbackFontFamily | undefined => {
  if (/\p{Extended_Pictographic}|\p{Regional_Indicator}|\u20E3/u.test(grapheme)) return 'Noto Color Emoji';
  if (/\p{Script_Extensions=Arabic}/u.test(grapheme)) return 'Noto Sans Arabic';
  if (/\p{Script_Extensions=Devanagari}/u.test(grapheme)) return 'Noto Sans Devanagari';
  if (/\p{Script_Extensions=Hebrew}/u.test(grapheme)) return 'Noto Sans Hebrew';
  if (/\p{Script_Extensions=Thai}/u.test(grapheme)) return 'Noto Sans Thai';
  if (/\p{Script_Extensions=Hiragana}|\p{Script_Extensions=Katakana}/u.test(grapheme)) return 'Noto Sans JP';
  if (/\p{Script_Extensions=Hangul}/u.test(grapheme)) return 'Noto Sans KR';
  if (/\p{Script_Extensions=Han}/u.test(grapheme)) return hanFallbackFamily;
  if (/\p{Script_Extensions=Cyrillic}|\p{Script_Extensions=Greek}/u.test(grapheme)) return 'Noto Sans';
  return undefined;
};

export interface ProductionFontRun {
  family: string;
  sample: string;
  fallback: boolean;
}

export const resolveProductionFontRuns = (
  fontFamily: string,
  content: string
): ProductionFontRun[] => {
  const primaryFamily = resolveProductionFontFamily(fontFamily);
  const value = String(content || '');
  const firstJapaneseIndex = value.search(/\p{Script_Extensions=Hiragana}|\p{Script_Extensions=Katakana}/u);
  const firstKoreanIndex = value.search(/\p{Script_Extensions=Hangul}/u);
  const hanFallbackFamily = firstJapaneseIndex >= 0
    && (firstKoreanIndex < 0 || firstJapaneseIndex < firstKoreanIndex)
    ? 'Noto Sans JP'
    : firstKoreanIndex >= 0
      ? 'Noto Sans KR'
      : 'Noto Sans SC';
  const fallbackSamples = new Map<string, string>();
  let primarySample = '';

  for (const grapheme of segmentGraphemes(value)) {
    const fallbackFamily = fallbackFamilyForGrapheme(
      grapheme,
      hanFallbackFamily
    );
    if (!fallbackFamily || fallbackFamily === primaryFamily) {
      primarySample += grapheme;
      continue;
    }
    fallbackSamples.set(
      fallbackFamily,
      `${fallbackSamples.get(fallbackFamily) || ''}${grapheme}`
    );
  }

  return [
    {
      family: primaryFamily,
      sample: primarySample || 'Tiny Little Fox',
      fallback: false
    },
    ...Array.from(fallbackSamples, ([family, sample]) => ({
      family,
      sample,
      fallback: true
    }))
  ];
};

export const resolveProductionFontStack = (
  fontFamily: string,
  content = ''
): string[] => Array.from(new Set(
  resolveProductionFontRuns(fontFamily, content).map(run => run.family)
));

export type StoryTextDirection = 'ltr' | 'rtl';

export const resolveTextDirection = (content: string): StoryTextDirection => {
  for (const grapheme of segmentGraphemes(content)) {
    if (!/\p{Letter}|\p{Number}/u.test(grapheme)) continue;
    if (/\p{Script_Extensions=Arabic}|\p{Script_Extensions=Hebrew}/u.test(grapheme)) return 'rtl';
    return 'ltr';
  }
  return 'ltr';
};

const quoteCssFontFamily = (fontFamily: string) => {
  const escapedFamily = fontFamily
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
  return `"${escapedFamily}"`;
};

export const toCssFontFamily = (fontFamily: string, content = '') => (
  resolveProductionFontStack(fontFamily, content)
    .map(quoteCssFontFamily)
    .join(', ')
);

export const createStoryTextTypography = (
  fontFamily = 'Roboto',
  fontWeight = 'normal',
  fontStyle: StoryFontStyle = 'normal',
  padding = STORY_TEXT_PADDING
): StoryTextTypography => ({
  fontFamily: resolveProductionFontFamily(fontFamily),
  fontWeight: fontWeight || 'normal',
  fontStyle: fontStyle || 'normal',
  lineHeight: STORY_TEXT_LINE_HEIGHT,
  padding,
  fontAssetVersion: PRODUCTION_FONT_ASSET_VERSION
});

const normalizeTypography = (
  typography: StoryTextTypography
): StoryTextTypography => ({
  ...typography,
  fontFamily: resolveProductionFontFamily(typography.fontFamily),
  fontWeight: typography.fontWeight || 'normal',
  fontStyle: typography.fontStyle || 'normal',
  lineHeight: typography.lineHeight || STORY_TEXT_LINE_HEIGHT,
  padding: Number.isFinite(typography.padding)
    ? Math.max(0, typography.padding)
    : STORY_TEXT_PADDING,
  fontAssetVersion: typography.fontAssetVersion || PRODUCTION_FONT_ASSET_VERSION
});

const normalizeFontWeightForLoading = (fontWeight: string) => {
  if (fontWeight === 'normal') return '400';
  if (fontWeight === 'bold') return '700';
  return fontWeight || '400';
};

const fontLoadKey = (typography: StoryTextTypography, content: string) => [
  typography.fontFamily,
  normalizeFontWeightForLoading(typography.fontWeight),
  typography.fontAssetVersion,
  content
].join('|');

const fontVariantKey = (typography: StoryTextTypography) => [
  typography.fontFamily,
  normalizeFontWeightForLoading(typography.fontWeight),
  typography.fontAssetVersion
].join('|');

const faceLoadKey = (
  typography: StoryTextTypography,
  run: Pick<ProductionFontRun, 'family' | 'fallback'>
) => {
  const primaryWeight = normalizeFontWeightForLoading(typography.fontWeight);
  const fallbackWeight = Number.parseInt(primaryWeight, 10) >= 600 ? '700' : '400';
  const weight = run.family === 'Noto Color Emoji'
    ? '400'
    : run.fallback ? fallbackWeight : primaryWeight;
  return `${run.family}|${weight}|${typography.fontAssetVersion}`;
};

const contextualHanSamples = (sample: ProductionFontSample): ProductionFontSample[] => {
  const content = String(sample.content || '');
  const hasHan = /\p{Script_Extensions=Han}/u.test(content);
  const hasKanaOrHangul = /\p{Script_Extensions=Hiragana}|\p{Script_Extensions=Katakana}|\p{Script_Extensions=Hangul}/u.test(content);
  if (!hasHan || !hasKanaOrHangul) return [];

  // A split fragment may contain Han but no longer contain the Kana/Hangul
  // signal that selected its source font. Preload each deterministic Han face
  // so the fragment is measured with the same stack the renderer will use.
  return ['漢字', 'かな漢字', '한글漢字'].map(contentSample => ({
    ...sample,
    content: contentSample
  }));
};

const loadedFontSamples = new Map<string, Promise<void>>();

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, message: string) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
};

const loadProductionFontSample = (
  sample: ProductionFontSample,
  timeoutMs: number
) => {
  const typography = normalizeTypography(sample.typography);
  const content = String(sample.content || 'Tiny Little Fox');
  const key = fontLoadKey(typography, content);
  const cached = loadedFontSamples.get(key);
  if (cached) return cached;

  const loading = (async () => {
    if (typeof document === 'undefined' || !document.fonts) {
      throw new Error('Production font measurement requires the browser Font Loading API.');
    }

    if (!PRODUCTION_FONT_FAMILIES.has(typography.fontFamily)) {
      throw new Error(
        `“${typography.fontFamily}” is not a supported production storybook font.`
      );
    }

    const primaryWeight = normalizeFontWeightForLoading(typography.fontWeight);
    const runs = resolveProductionFontRuns(typography.fontFamily, content);
    await Promise.all(runs.map(async run => {
      const fallbackWeight = Number.parseInt(primaryWeight, 10) >= 600 ? '700' : '400';
      const weight = run.family === 'Noto Color Emoji'
        ? '400'
        : run.fallback ? fallbackWeight : primaryWeight;
      const fontDefinition = `${weight} ${Math.max(1, sample.fontSize)}px "${run.family}"`;
      const faces = await withTimeout(
        document.fonts.load(fontDefinition, run.sample),
        timeoutMs,
        `Timed out loading the production font “${run.family}”.`
      );

      const hasLoadedFace = Array.from(document.fonts as any).some((face: any) => (
        stripFontQuotes(String(face.family || '')) === run.family
        && face.status === 'loaded'
      ));
      if (faces.length === 0 || !hasLoadedFace) {
        throw new Error(
          `The production font “${run.family}” did not load. Layout was not changed.`
        );
      }
    }));
    await withTimeout(
      document.fonts.ready,
      timeoutMs,
      `Timed out waiting for the production font “${typography.fontFamily}”.`
    );
  })();

  loadedFontSamples.set(key, loading);
  loading.catch(() => loadedFontSamples.delete(key));
  return loading;
};

export const createProductionTextBlockMeasurer = async (
  samples: ProductionFontSample[],
  timeoutMs = 10000
): Promise<TextBlockMeasurer> => {
  const normalizedSamples = samples.map(sample => ({
    ...sample,
    typography: normalizeTypography(sample.typography)
  }));
  const fontLoadSamples = [
    ...normalizedSamples,
    ...normalizedSamples.flatMap(contextualHanSamples)
  ];
  await Promise.all(fontLoadSamples.map(sample => (
    loadProductionFontSample(sample, timeoutMs)
  )));
  const loadedFaces = new Set(fontLoadSamples.flatMap(sample => (
    resolveProductionFontRuns(sample.typography.fontFamily, String(sample.content || 'Tiny Little Fox'))
      .map(run => faceLoadKey(sample.typography, run))
  )));

  return ({ content, width, fontSize, typography }) => {
    if (typeof document === 'undefined' || !document.body) {
      throw new Error('Production text measurement requires a rendered browser document.');
    }

    const normalized = normalizeTypography(typography);
    const measuredContent = String(content || 'Tiny Little Fox');
    const isAuthorizedFragment = normalizedSamples.some(sample => (
      fontVariantKey(sample.typography) === fontVariantKey(normalized)
      && String(sample.content || 'Tiny Little Fox').includes(measuredContent)
    ));
    if (!isAuthorizedFragment) {
      throw new Error(
        `The text sample for “${normalized.fontFamily}” was not loaded before measurement.`
      );
    }
    const missingFace = resolveProductionFontRuns(normalized.fontFamily, measuredContent)
      .find(run => !loadedFaces.has(faceLoadKey(normalized, run)));
    if (missingFace) {
      throw new Error(
        `The production font “${missingFace.family}” was not loaded for this text fragment.`
      );
    }

    const probe = document.createElement('div');
    Object.assign(probe.style, {
      position: 'fixed',
      left: '-100000px',
      top: '0',
      width: `${Math.max(1, width)}px`,
      height: 'auto',
      visibility: 'hidden',
      pointerEvents: 'none',
      boxSizing: 'border-box',
      margin: '0',
      border: '0',
      padding: `${normalized.padding}px`,
      fontFamily: toCssFontFamily(normalized.fontFamily, measuredContent),
      fontSize: `${fontSize}px`,
      fontWeight: normalized.fontWeight,
      fontStyle: normalized.fontStyle,
      lineHeight: String(normalized.lineHeight),
      textAlign: 'left',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'break-word',
      wordBreak: 'break-word'
    });
    probe.textContent = String(content || '');

    document.body.appendChild(probe);
    try {
      const measuredHeight = Math.ceil(Math.max(
        probe.scrollHeight,
        probe.getBoundingClientRect().height
      ));
      const contentHeight = Math.max(0, measuredHeight - normalized.padding * 2);
      const lineCount = Math.max(
        1,
        Math.round(contentHeight / (fontSize * normalized.lineHeight))
      );

      if (!Number.isFinite(measuredHeight) || measuredHeight <= 0) {
        throw new Error('The browser returned an invalid production text measurement.');
      }

      return {
        height: measuredHeight,
        lineCount,
        typography: normalized
      };
    } finally {
      probe.remove();
    }
  };
};

const splitLongSegment = (
  segment: string,
  maxWidth: number,
  measure: (value: string) => number
) => {
  const graphemes = segmentGraphemes(segment);
  const pieces: string[] = [];
  let current = '';

  for (const grapheme of graphemes) {
    const candidate = current + grapheme;
    if (current && measure(candidate) > maxWidth) {
      pieces.push(current);
      current = grapheme;
    } else {
      current = candidate;
    }
  }
  if (current || pieces.length === 0) pieces.push(current);
  return pieces;
};

export const wrapTextForCanvas = (
  content: string,
  maxWidth: number,
  measure: (value: string) => number
): string[] => {
  const lines: string[] = [];

  for (const paragraph of String(content || '').split('\n')) {
    if (paragraph.length === 0) {
      lines.push('');
      continue;
    }

    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    let currentLine = '';

    for (const word of words) {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      if (measure(candidate) <= maxWidth) {
        currentLine = candidate;
        continue;
      }

      if (currentLine) {
        lines.push(currentLine);
        currentLine = '';
      }

      if (measure(word) <= maxWidth) {
        currentLine = word;
        continue;
      }

      const pieces = splitLongSegment(word, maxWidth, measure);
      lines.push(...pieces.slice(0, -1));
      currentLine = pieces[pieces.length - 1];
    }

    lines.push(currentLine);
  }

  return lines.length > 0 ? lines : [''];
};
